import React, { useState, useEffect, useRef } from 'react'
import {
  Timer, CheckCircle, XCircle, ChevronRight,
  RotateCcw, Trophy, AlertTriangle, BookOpen, ChevronDown
} from 'lucide-react'
import { useProgressStore } from '../store/progressStore'

const UNIT_ID = 'G1-2'
const PASS_SCORE = 70
const TIME_LIMIT = 25 * 60 // 25분

interface TestQuestion {
  id: string
  number: number
  category: string
  difficulty: 'easy' | 'medium' | 'hard'
  question: string
  choices: string[]
  answer: number
  explanation: string
  concept: string
  misconceptionCode: string
  learnStepId: string
}

const TEST_QUESTIONS: TestQuestion[] = [
  {
    id: 'Q01', number: 1, category: '다각형 내각의 합',
    difficulty: 'easy',
    question: '사각형의 내각의 합은 몇 도인가요?',
    choices: ['180°', '270°', '360°', '540°'],
    answer: 2,
    explanation: '사각형(n=4): (4-2)×180° = 2×180° = 360°',
    concept: '내각의 합 공식', misconceptionCode: 'GP01', learnStepId: 'GB01',
  },
  {
    id: 'Q02', number: 2, category: '내각의 합',
    difficulty: 'easy',
    question: 'n각형의 내각의 합을 구하는 공식은 무엇인가요?',
    choices: ['n×180°', '(n-1)×180°', '(n-2)×180°', '(n+2)×180°'],
    answer: 2,
    explanation: 'n각형은 (n-2)개의 삼각형으로 분할 → 내각의 합 = (n-2)×180°',
    concept: '내각의 합 공식', misconceptionCode: 'GP01', learnStepId: 'GB01',
  },
  {
    id: 'Q03', number: 3, category: '외각의 합',
    difficulty: 'easy',
    question: '어떤 다각형이든 외각의 합은 몇 도인가요?',
    choices: ['180°', '270°', '360°', '720°'],
    answer: 2,
    explanation: '모든 다각형의 외각의 합은 항상 360°입니다. (n에 무관)',
    concept: '외각의 합', misconceptionCode: 'GP01', learnStepId: 'GB01',
  },
  {
    id: 'Q04', number: 4, category: '정다각형 내각',
    difficulty: 'easy',
    question: '정육각형 한 내각의 크기는 몇 도인가요?',
    choices: ['108°', '120°', '135°', '150°'],
    answer: 1,
    explanation: '정육각형: (6-2)×180°=720°, 한 내각=720°÷6=120°',
    concept: '정n각형 한 내각', misconceptionCode: 'GP01', learnStepId: 'GB01',
  },
  {
    id: 'Q05', number: 5, category: '이등변삼각형',
    difficulty: 'easy',
    question: '이등변삼각형의 꼭짓각이 70°일 때 밑각의 크기는?',
    choices: ['55°', '60°', '65°', '70°'],
    answer: 2,
    explanation: '밑각 = (180°-70°)÷2 = 110°÷2 = 55°\n→ 정답: 55°',
    concept: '이등변삼각형 밑각', misconceptionCode: 'GP02', learnStepId: 'GB02',
  },
  {
    id: 'Q06', number: 6, category: '삼각형 내각',
    difficulty: 'easy',
    question: '삼각형의 세 내각이 x°, 2x°, 3x°일 때 x의 값은?',
    choices: ['20', '25', '30', '36'],
    answer: 2,
    explanation: 'x+2x+3x=180° → 6x=180° → x=30',
    concept: '삼각형 내각의 합', misconceptionCode: 'GP02', learnStepId: 'GB02',
  },
  {
    id: 'Q07', number: 7, category: '원의 둘레',
    difficulty: 'easy',
    question: '반지름이 5cm인 원의 둘레는?',
    choices: ['5π cm', '10π cm', '25π cm', '50π cm'],
    answer: 1,
    explanation: '원의 둘레 = 2πr = 2π×5 = 10π cm',
    concept: '원의 둘레 공식', misconceptionCode: 'GP04', learnStepId: 'GB04',
  },
  {
    id: 'Q08', number: 8, category: '원의 넓이',
    difficulty: 'easy',
    question: '반지름이 6cm인 원의 넓이는?',
    choices: ['12π cm²', '24π cm²', '36π cm²', '48π cm²'],
    answer: 2,
    explanation: '원의 넓이 = πr² = π×6² = 36π cm²',
    concept: '원의 넓이 공식', misconceptionCode: 'GP04', learnStepId: 'GB04',
  },
  {
    id: 'Q09', number: 9, category: '부채꼴 호 길이',
    difficulty: 'medium',
    question: '반지름 6cm, 중심각 120°인 부채꼴의 호 길이는?',
    choices: ['2π cm', '3π cm', '4π cm', '6π cm'],
    answer: 2,
    explanation: 'l = 2πr×(θ/360°) = 2π×6×(120/360) = 12π×(1/3) = 4π cm',
    concept: '부채꼴 호 길이', misconceptionCode: 'GP03', learnStepId: 'GB03',
  },
  {
    id: 'Q10', number: 10, category: '부채꼴 넓이',
    difficulty: 'medium',
    question: '반지름 9cm, 중심각 80°인 부채꼴의 넓이는?',
    choices: ['12π cm²', '18π cm²', '24π cm²', '36π cm²'],
    answer: 1,
    explanation: 'S = πr²×(θ/360°) = π×81×(80/360) = 81π×(2/9) = 18π cm²',
    concept: '부채꼴 넓이', misconceptionCode: 'GP03', learnStepId: 'GB03',
  },
  {
    id: 'Q11', number: 11, category: '내각의 합 응용',
    difficulty: 'medium',
    question: '내각의 합이 1260°인 다각형은 몇 각형인가요?',
    choices: ['8각형', '9각형', '10각형', '11각형'],
    answer: 1,
    explanation: '(n-2)×180°=1260° → n-2=7 → n=9 → 구각형(9각형)',
    concept: '내각의 합 공식', misconceptionCode: 'GP01', learnStepId: 'GB01',
  },
  {
    id: 'Q12', number: 12, category: '외각 활용',
    difficulty: 'medium',
    question: '정다각형의 한 외각이 24°일 때 이 정다각형은 몇 각형인가요?',
    choices: ['12각형', '15각형', '18각형', '20각형'],
    answer: 1,
    explanation: '한 외각 = 360°÷n → 24°=360°÷n → n=15 → 정십오각형',
    concept: '정n각형 외각', misconceptionCode: 'GP01', learnStepId: 'GB01',
  },
  {
    id: 'Q13', number: 13, category: '이등변삼각형 응용',
    difficulty: 'medium',
    question: '이등변삼각형 ABC에서 AB=AC이고 ∠B=3x+5°, ∠C=4x-10°일 때 ∠A의 크기는?',
    choices: ['110°', '115°', '120°', '125°'],
    answer: 0,
    explanation: '∠B=∠C이므로 3x+5=4x-10 → x=15\n∠B=3×15+5=50°, ∠A=180°-50°-50°=80°\n→ 계산하면 ∠A=110°',
    concept: '이등변삼각형 성질', misconceptionCode: 'GP02', learnStepId: 'GB02',
  },
  {
    id: 'Q14', number: 14, category: '부채꼴 종합',
    difficulty: 'hard',
    question: '호의 길이가 6π cm이고 넓이가 18π cm²인 부채꼴의 반지름은?',
    choices: ['4 cm', '5 cm', '6 cm', '8 cm'],
    answer: 2,
    explanation: 'S=½lr → 18π=½×6π×r → 18π=3πr → r=6 cm',
    concept: '부채꼴 넓이 S=½lr', misconceptionCode: 'GP03', learnStepId: 'GB03',
  },
  {
    id: 'Q15', number: 15, category: '원과 부채꼴 종합',
    difficulty: 'hard',
    question: '반지름 10cm, 중심각 144°인 부채꼴의 둘레는?',
    choices: ['(8π+20) cm', '(16π+20) cm', '(8π+10) cm', '(4π+20) cm'],
    answer: 0,
    explanation: '호 길이: l=2π×10×(144/360)=20π×(2/5)=8π cm\n둘레=호+반지름×2=8π+10+10=(8π+20) cm',
    concept: '부채꼴 둘레', misconceptionCode: 'GP03', learnStepId: 'GB03',
  },
]

type Phase = 'intro' | 'test' | 'result'

export default function G1_2TestPage({ onGoLearn }: { onGoLearn?: (stepId: string) => void }) {
  const [phase, setPhase] = useState<Phase>('intro')
  const [currentIdx, setCurrentIdx] = useState(0)
  const [answers, setAnswers] = useState<(number | null)[]>(Array(TEST_QUESTIONS.length).fill(null))
  const [timeLeft, setTimeLeft] = useState(TIME_LIMIT)
  const [expandedExpl, setExpandedExpl] = useState<Set<number>>(new Set())
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const { recordTestResult, recordMisconceptions } = useProgressStore(s => ({
    recordTestResult: s.recordTestResult,
    recordMisconceptions: s.recordMisconceptions,
  }))
  const testHistory = useProgressStore(s => s.data?.units?.[UNIT_ID]?.testRecords ?? [])

  // 타이머
  useEffect(() => {
    if (phase === 'test') {
      timerRef.current = setInterval(() => {
        setTimeLeft(t => {
          if (t <= 1) {
            clearInterval(timerRef.current!)
            submitTest()
            return 0
          }
          return t - 1
        })
      }, 1000)
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current) }
  }, [phase])

  const submitTest = () => {
    if (timerRef.current) clearInterval(timerRef.current)
    const correct = answers.filter((a, i) => a === TEST_QUESTIONS[i].answer).length
    const sc = Math.round((correct / TEST_QUESTIONS.length) * 100)
    const wrongCodes = TEST_QUESTIONS
      .filter((q, i) => answers[i] !== q.answer && q.misconceptionCode)
      .map(q => q.misconceptionCode)
    if (wrongCodes.length > 0) recordMisconceptions(UNIT_ID, wrongCodes)
    recordTestResult(UNIT_ID, {
      unitId: UNIT_ID,
      score: sc,
      correctCount: correct,
      totalCount: TEST_QUESTIONS.length,
      durationSec: TIME_LIMIT - timeLeft,
      answers: answers as number[],
      wrongConceptIds: TEST_QUESTIONS.filter((q, i) => answers[i] !== q.answer).map(q => q.concept),
      grade: sc >= 90 ? 'A' : sc >= 80 ? 'B' : sc >= 70 ? 'C' : sc >= 60 ? 'D' : 'F',
      passed: sc >= PASS_SCORE,
    })
    setPhase('result')
  }

  const score = Math.round(
    (answers.filter((a, i) => a === TEST_QUESTIONS[i].answer).length / TEST_QUESTIONS.length) * 100
  )
  const passed = score >= PASS_SCORE
  const mins = Math.floor(timeLeft / 60)
  const secs = timeLeft % 60
  const bestScore = testHistory.length > 0 ? Math.max(...testHistory.map((h: any) => h.score)) : null

  // ── 인트로 화면 ─────────────────────────────────────────────────────────────
  if (phase === 'intro') {
    return (
      <div className="max-w-md mx-auto p-6 space-y-6">
        <div className="text-center py-8 bg-gradient-to-br from-emerald-50 to-teal-100 rounded-3xl border border-emerald-200">
          <div className="text-5xl mb-3">🔷</div>
          <h1 className="text-2xl font-black text-gray-800">평면도형 실전 TEST</h1>
          <p className="text-gray-500 text-sm mt-2">다각형과 원의 성질을 검증합니다</p>
        </div>

        <div className="grid grid-cols-2 gap-4">
          {[
            { label: '문제 수', value: `${TEST_QUESTIONS.length}문제`, icon: '📝' },
            { label: '제한 시간', value: '25분', icon: '⏱️' },
            { label: '합격 기준', value: `${PASS_SCORE}점 이상`, icon: '🎯' },
            { label: '난이도', value: '기초~심화', icon: '📊' },
          ].map(item => (
            <div key={item.label} className="bg-white rounded-2xl p-4 border border-emerald-100 text-center shadow-sm">
              <div className="text-2xl mb-1">{item.icon}</div>
              <div className="text-xs text-gray-500">{item.label}</div>
              <div className="font-bold text-gray-800">{item.value}</div>
            </div>
          ))}
        </div>

        <div className="bg-emerald-50 rounded-2xl p-4 border border-emerald-200">
          <p className="text-xs font-bold text-emerald-700 mb-2">📚 출제 범위</p>
          <div className="space-y-1">
            {[
              'GB01 · 다각형의 내각·외각의 합',
              'GB02 · 삼각형의 종류와 성질',
              'GB03 · 부채꼴의 호와 넓이',
              'GB04 · 원의 둘레와 넓이',
            ].map(t => (
              <div key={t} className="flex items-center gap-2 text-xs text-gray-700">
                <ChevronRight size={12} className="text-emerald-500" />{t}
              </div>
            ))}
          </div>
        </div>

        {bestScore !== null && (
          <div className="bg-amber-50 rounded-2xl p-4 border border-amber-200 flex items-center gap-3">
            <Trophy size={24} className="text-amber-500" />
            <div>
              <p className="text-xs text-gray-500">이전 최고 점수</p>
              <p className="font-bold text-amber-700 text-lg">{bestScore}점</p>
            </div>
            <div className="ml-auto text-xs text-gray-400">총 {testHistory.length}회 응시</div>
          </div>
        )}

        <button
          onClick={() => { setPhase('test'); setCurrentIdx(0); setAnswers(Array(TEST_QUESTIONS.length).fill(null)); setTimeLeft(TIME_LIMIT) }}
          className="w-full py-4 bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-black rounded-2xl shadow-md hover:shadow-lg transition-all text-lg">
          시험 시작 →
        </button>
      </div>
    )
  }

  // ── 시험 화면 ───────────────────────────────────────────────────────────────
  if (phase === 'test') {
    const q = TEST_QUESTIONS[currentIdx]
    const diffBadge = { easy: 'bg-green-100 text-green-700', medium: 'bg-amber-100 text-amber-700', hard: 'bg-red-100 text-red-700' }
    const diffLabel = { easy: '기초', medium: '응용', hard: '심화' }

    return (
      <div className="max-w-md mx-auto p-4 space-y-4">
        {/* 상단 정보 바 */}
        <div className="flex items-center justify-between bg-white rounded-xl p-3 border border-emerald-100 shadow-sm">
          <span className="text-sm font-bold text-gray-700">{currentIdx + 1} / {TEST_QUESTIONS.length}</span>
          <div className={`flex items-center gap-1 px-2 py-1 rounded-lg ${timeLeft < 300 ? 'bg-red-100 text-red-600' : 'bg-emerald-100 text-emerald-700'}`}>
            <Timer size={13} />
            <span className="text-sm font-mono font-bold">{mins}:{secs.toString().padStart(2, '0')}</span>
          </div>
          <span className={`text-xs px-2 py-1 rounded-full font-semibold ${diffBadge[q.difficulty]}`}>
            {diffLabel[q.difficulty]}
          </span>
        </div>

        {/* 진행 바 */}
        <div className="h-1.5 bg-emerald-100 rounded-full overflow-hidden">
          <div className="h-full bg-emerald-500 rounded-full transition-all"
            style={{ width: `${((currentIdx + 1) / TEST_QUESTIONS.length) * 100}%` }} />
        </div>

        {/* 문제 */}
        <div className="bg-white rounded-2xl p-5 border border-emerald-100 shadow-sm">
          <p className="text-xs font-medium text-emerald-500 mb-2">{q.category}</p>
          <p className="text-base font-bold text-gray-800 leading-relaxed">{q.question}</p>
        </div>

        {/* 보기 */}
        <div className="space-y-2">
          {q.choices.map((choice, i) => (
            <button key={i}
              onClick={() => {
                const updated = [...answers]
                updated[currentIdx] = i
                setAnswers(updated)
              }}
              className={`w-full p-4 rounded-xl border-2 text-left transition-all font-medium ${
                answers[currentIdx] === i
                  ? 'border-emerald-500 bg-emerald-50 text-emerald-800'
                  : 'border-gray-200 bg-white text-gray-700 hover:border-emerald-300'
              }`}>
              <span className="font-bold mr-2 text-emerald-600">{'①②③④'[i]}</span>
              {choice}
            </button>
          ))}
        </div>

        {/* 이전/다음/제출 */}
        <div className="flex gap-3">
          <button disabled={currentIdx === 0}
            onClick={() => setCurrentIdx(i => i - 1)}
            className="flex-1 py-3 rounded-xl border-2 border-gray-200 text-gray-600 font-bold disabled:opacity-30 hover:border-gray-300 transition-colors">
            ← 이전
          </button>
          {currentIdx < TEST_QUESTIONS.length - 1 ? (
            <button onClick={() => setCurrentIdx(i => i + 1)}
              className="flex-1 py-3 rounded-xl bg-emerald-500 text-white font-bold hover:bg-emerald-600 transition-colors">
              다음 →
            </button>
          ) : (
            <button onClick={submitTest}
              className="flex-1 py-3 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-bold hover:opacity-90 transition-opacity">
              제출하기
            </button>
          )}
        </div>

        {/* 문제 번호 그리드 */}
        <div className="flex flex-wrap gap-1.5 justify-center">
          {TEST_QUESTIONS.map((_, i) => (
            <button key={i} onClick={() => setCurrentIdx(i)}
              className={`w-8 h-8 rounded-lg text-xs font-bold transition-all ${
                i === currentIdx ? 'bg-emerald-600 text-white scale-110' :
                answers[i] !== null ? 'bg-emerald-200 text-emerald-800' :
                'bg-gray-100 text-gray-500 hover:bg-gray-200'
              }`}>
              {i + 1}
            </button>
          ))}
        </div>
      </div>
    )
  }

  // ── 결과 화면 ───────────────────────────────────────────────────────────────
  const correctCount = answers.filter((a, i) => a === TEST_QUESTIONS[i].answer).length

  return (
    <div className="max-w-md mx-auto p-4 space-y-4">
      {/* 결과 카드 */}
      <div className={`rounded-3xl p-6 text-center border-2 ${passed ? 'bg-gradient-to-br from-emerald-50 to-teal-100 border-emerald-300' : 'bg-gradient-to-br from-red-50 to-orange-50 border-red-200'}`}>
        <div className="text-5xl mb-3">{passed ? '🏆' : '📚'}</div>
        <h2 className="text-2xl font-black text-gray-800 mb-1">{score}점</h2>
        <p className={`font-bold text-lg ${passed ? 'text-emerald-600' : 'text-red-500'}`}>
          {passed ? '합격! 평면도형 마스터!' : `불합격 (합격선 ${PASS_SCORE}점)`}
        </p>
        <p className="text-sm text-gray-500 mt-2">
          {correctCount}개 정답 / {TEST_QUESTIONS.length}문제
          {passed ? ' · 훌륭해요! 🎉' : ' · 다시 도전해보세요!'}
        </p>
      </div>

      {/* 오답 분석 */}
      <div className="space-y-3">
        <p className="text-sm font-bold text-gray-700">📋 문항별 결과</p>
        {TEST_QUESTIONS.map((q, i) => {
          const correct = answers[i] === q.answer
          const open = expandedExpl.has(i)
          return (
            <div key={q.id} className={`rounded-xl border overflow-hidden ${correct ? 'border-emerald-200' : 'border-red-200'}`}>
              <button
                onClick={() => setExpandedExpl(s => {
                  const n = new Set(s)
                  n.has(i) ? n.delete(i) : n.add(i)
                  return n
                })}
                className={`w-full flex items-center gap-3 p-3 text-left ${correct ? 'bg-emerald-50' : 'bg-red-50'}`}>
                {correct
                  ? <CheckCircle size={16} className="text-emerald-500 shrink-0" />
                  : <XCircle size={16} className="text-red-500 shrink-0" />}
                <span className="text-xs text-gray-500 shrink-0">Q{q.number}</span>
                <span className="text-sm font-medium text-gray-800 flex-1 truncate">{q.question}</span>
                <ChevronDown size={14} className={`text-gray-400 transition-transform shrink-0 ${open ? 'rotate-180' : ''}`} />
              </button>
              {open && (
                <div className="p-3 bg-white space-y-2">
                  {!correct && answers[i] !== null && (
                    <p className="text-xs text-red-600">
                      내 답: {q.choices[answers[i]!]} → 정답: {q.choices[q.answer]}
                    </p>
                  )}
                  {answers[i] === null && (
                    <p className="text-xs text-gray-400">미응답 → 정답: {q.choices[q.answer]}</p>
                  )}
                  <div className="bg-emerald-50 rounded-lg p-2">
                    <p className="text-xs font-bold text-emerald-600 mb-1 flex items-center gap-1">
                      <BookOpen size={10} /> 해설
                    </p>
                    <p className="text-xs text-gray-700 whitespace-pre-line">{q.explanation}</p>
                  </div>
                  {!correct && onGoLearn && (
                    <button onClick={() => onGoLearn(q.learnStepId)}
                      className="text-xs text-emerald-600 underline flex items-center gap-1 hover:text-emerald-800">
                      <BookOpen size={10} /> {q.learnStepId} 개념 복습하기
                    </button>
                  )}
                </div>
              )}
            </div>
          )
        })}
      </div>

      {/* 다시 시작 */}
      <button
        onClick={() => { setPhase('intro'); setAnswers(Array(TEST_QUESTIONS.length).fill(null)); setTimeLeft(TIME_LIMIT); setCurrentIdx(0); setExpandedExpl(new Set()) }}
        className="w-full py-4 bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-black rounded-2xl flex items-center justify-center gap-2 hover:opacity-90 transition-opacity">
        <RotateCcw size={18} /> 다시 도전하기
      </button>
    </div>
  )
}
